/**
 * PrintStyles Component
 *
 * Provides consistent print styling across all printable documents.
 * Removes browser-generated headers/footers and provides clean print output.
 *
 * Usage:
 *   <PrintStyles orientation="portrait" />
 *   <PrintStyles orientation="landscape" />
 */

const PrintStyles = ({ orientation = 'portrait' }) => {
  const pageSize = orientation === 'landscape' ? 'A4 landscape' : 'A4'

  return (
    <style>
      {`
        @media print {
          /* Hide everything by default */
          body * {
            visibility: hidden;
          }

          /* Show only the printable content */
          .print-content, .print-content * {
            visibility: visible;
          }

          /* Position the print content */
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }

          /* Remove browser headers/footers by setting margin to 0 */
          @page {
            size: ${pageSize};
            margin: 0;
          }

          /* Add our own margins to the body */
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Ensure content has proper spacing */
          .print-content {
            padding: 15mm !important;
            margin: 0 !important;
            max-width: none !important;
            box-sizing: border-box !important;
          }

          /* Prevent page breaks inside important elements */
          .no-break {
            page-break-inside: avoid;
          }

          /* Force page break before element */
          .page-break-before {
            page-break-before: always;
          }

          /* Table styling for print */
          table {
            font-size: 10px !important;
            border-collapse: collapse !important;
          }

          .MuiTableCell-root {
            padding: 6px 8px !important;
            border: 1px solid #ddd !important;
          }

          /* Hide non-print elements */
          .no-print, .MuiToolbar-root, .print-controls {
            display: none !important;
          }

          /* QR code should always be visible */
          .document-qr-code {
            visibility: visible !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Ensure images print correctly */
          img {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            max-width: 100% !important;
          }

          /* Preserve background colors */
          .MuiChip-root, .status-chip {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }

        /* Screen preview styling */
        @media screen {
          .print-content {
            width: fit-content;
            min-width: ${orientation === 'landscape' ? '297mm' : '210mm'};
            margin: 0 auto;
            padding: 15mm;
            background-color: white;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }
        }
      `}
    </style>
  )
}

export default PrintStyles
